import type { CSSProperties } from 'react';
import { DieCube } from '@/components/dice/Die';
import { mulberry32 } from '@/engine/rng';
import { cn } from '@/lib/utils';

/** One drifting die. Positions are percentages of the field. */
export interface DieParticle {
  value: number;
  left: number;
  top: number;
  size: number; // px
  duration: number; // s, one float cycle
  spin: number; // s, one full tumble
  delay: number; // s (negative starts mid-flight)
  tone: 0 | 1 | 2;
}

/** Deterministic particle set so a layout never shifts between loads. */
export function generateParticles(seed: number, count: number): DieParticle[] {
  const rnd = mulberry32(seed);
  const out: DieParticle[] = [];
  for (let i = 0; i < count; i++) {
    // Stratify horizontally so the field is evenly covered.
    const band = (i + rnd()) / count;
    out.push({
      value: 1 + Math.floor(rnd() * 6),
      left: Math.round(band * 100),
      top: Math.round(6 + rnd() * 88),
      size: Math.round(14 + rnd() * 30),
      duration: Math.round(14 + rnd() * 14),
      spin: Math.round(9 + rnd() * 14),
      delay: -Math.round(rnd() * 20),
      tone: (i % 3) as 0 | 1 | 2,
    });
  }
  return out;
}

/**
 * A field of slowly tumbling 3D dice. Purely decorative; pointer-events off
 * and hidden from assistive tech. Motion is CSS-only and honors reduced motion
 * through the global rules.
 */
export function DiceField({
  particles,
  className,
}: {
  particles: DieParticle[];
  className?: string;
}) {
  return (
    <div className={cn('dfield', className)} aria-hidden>
      {particles.map((p, i) => {
        const style = {
          left: `${p.left}%`,
          top: `${p.top}%`,
          '--p-size': `${p.size}px`,
          '--p-dur': `${p.duration}s`,
          '--p-spin': `${p.spin}s`,
          '--p-delay': `${p.delay}s`,
        } as CSSProperties;
        return (
          <div key={i} className="dfield-die" data-tone={p.tone} style={style}>
            <div className="dfield-float">
              <div className="die-scene">
                <div className="dfield-spin">
                  <DieCube value={p.value} instant />
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const BACKGROUND = generateParticles(0x5eed, 18);

/** Fixed, full-viewport ambient layer behind every page. */
export function BackgroundDice() {
  return <DiceField particles={BACKGROUND} className="dfield-bg" />;
}
